import { SelectCategorieDepense } from '../components/shared/SelectCategorieDepense';
import { useCallback, useEffect, useState } from 'react';
import { Camera, Plus, Download } from '../lib/icons';
import { toast } from 'sonner';
import {
  fetchOperationsCoursier,
  creerOperationCoursier,
  fetchTiers,
  fetchPersonnel,
  fetchCompteTiers,
  fetchFinances,
  nomTiers,
  TYPES_OPERATION,
  SENS_TIERS,
  MOTIFS_PAIE,
} from '../lib/dataProvider';
import { compresserImage } from '../lib/image';
import { deposerDocument } from '../lib/docs';
import { useAuth } from '../lib/auth';
import { caisseImposee } from '../lib/permissions';
import { fmt, PageTitle, StatBlock } from '../components/shared/badges';
import { ContactCombobox } from '../components/shared/ContactCombobox';
import { PeriodeFiltre, dansPeriode } from '../components/shared/PeriodeFiltre';
import { DepensesCanoniques } from '../components/canonique/BlocsCanoniques';

const inputCls =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';
const th = 'px-3 py-2 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500';

const COULEURS = {
  'En attente': 'bg-amber-50 text-amber-800',
  Validée: 'bg-emerald-50 text-emerald-800',
  Refusée: 'bg-red-50 text-red-700',
};

export default function Depenses() {
  const aujourdhui = new Date().toISOString().slice(0, 10);
  const { user } = useAuth();
  const [ops, setOps] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [soldeEmploye, setSoldeEmploye] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [f, setF] = useState({
    date: aujourdhui,
    typeOperation: 'frais',
    montant: '',
    categorie: '',
    libelle: '',
    tiersId: '',
    sensTiers: SENS_TIERS[0],
    employeId: '',
    motifPaie: MOTIFS_PAIE[0],
    justificatif: null,
    compteId: '',
  });
  const [comptes, setComptes] = useState([]);
  const caisseFixe = caisseImposee(user?.role);
  const load = useCallback(async () => {
    setOps(await fetchOperationsCoursier());
    setTiers(await fetchTiers());
    setPersonnel(await fetchPersonnel());
    const soldes = (await fetchFinances()).soldes;
    setComptes(caisseFixe ? soldes.filter((c) => c.id === caisseFixe) : soldes);
    if (caisseFixe) setF((p) => ({ ...p, compteId: caisseFixe }));
  }, [caisseFixe]);
  useEffect(() => {
    load();
  }, [load]);

  const choisirEmploye = async (employeId) => {
    setF((p) => ({ ...p, employeId }));
    if (!employeId) return setSoldeEmploye(null);
    const c = await fetchCompteTiers(employeId);
    setSoldeEmploye(c.solde);
  };

  const charger = async (file) => {
    if (!file) return;
    const { error, url, doublon, document: doc } = await deposerDocument(file, {
      source: 'recu_coursier',
      deposePar: user?.name,
    });
    if (error) return toast.error(error);
    setF((p) => ({ ...p, justificatif: url, documentId: doc.id }));
    toast[doublon ? 'warning' : 'success'](
      doublon ? `Ce reçu est déjà archivé (${doc.nom}) — document existant réutilisé` : 'Photo du reçu attachée et archivée'
    );
  };

  const submit = async (e) => {
    e.preventDefault();
    const res = await creerOperationCoursier({ ...f, saisiPar: user?.name });
    if (res.error) return toast.error(res.error);
    const mt = fmt(Number(f.montant), 3);
    if (res.statut === 'validee') {
      toast.success(`Opération de ${mt} TND validée et imputée`);
    } else if (res.statut === 'validation_echouee') {
      toast.error(
        res.indetermine
          ? `Opération de ${mt} TND ENREGISTRÉE sous ${res.row?.id} — validation au RÉSULTAT INDÉTERMINÉ : ` +
            `${res.detail} Ne la ressaisissez pas : vérifiez la base depuis le bandeau rouge.`
          : `Opération de ${mt} TND ENREGISTRÉE sous ${res.row?.id}, mais NON validée : ${res.detail} ` +
            'Ne la ressaisissez pas : validez-la depuis l’écran Validations.',
        { duration: 14000 }
      );
    } else {
      toast.success(`Opération de ${mt} TND enregistrée, en attente de validation`);
    }
    setF({ ...f, montant: '', libelle: '', categorie: '', tiersId: '', employeId: '', justificatif: null });
    setSoldeEmploye(null);
    load();
  };

  const enAttente = ops.filter((o) => o.statut === 'En attente');
  const validees = ops.filter((o) => o.statut === 'Validée');
  const opsVisibles = ops.filter((o) => dansPeriode(o.date, du, au));

  const exportOps = () => {
    const head = ['Réf', 'Date', 'Type', 'Bénéficiaire / catégorie', 'Motif', 'Montant (TND)', 'Statut'];
    const body = opsVisibles.map((o) =>
      [
        o.id,
        o.date,
        TYPES_OPERATION.find((t) => t.id === o.typeOperation)?.label ?? o.typeOperation,
        o.typeOperation === 'tiers'
          ? `${nomTiers(o.tiersId)} · ${o.sensTiers}`
          : o.typeOperation === 'personnel'
            ? `${nomTiers(o.employeId)} · ${o.motifPaie}`
            : o.categorie ?? '',
        (o.libelle ?? '').replace(/;/g, ','),
        o.montant,
        o.statut,
      ].join(';')
    );
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${[head.join(';'), ...body].join('\n')}`], { type: 'text/csv;charset=utf-8;' }));
    a.download = du || au ? `depenses_${du || 'debut'}_${au || 'fin'}.csv` : 'depenses.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Export CSV — ${opsVisibles.length} ligne(s)`);
  };

  return (
    <div data-testid="depenses-page" className="space-y-6">
      <PageTitle
        emoji="🧾"
        titre="Dépenses & opérations"
        sous="Frais généraux, règlements de tiers et paiements du personnel — soumis à validation du gérant"
      />

      <DepensesCanoniques tiers={tiers} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatBlock testid="stat-attente" titre="En attente de validation" accent="border-l-amber-500" items={[['Opérations', fmt(enAttente.length)], ['Montant (TND)', fmt(enAttente.reduce((s, o) => s + o.montant, 0), 3)]]} />
        <StatBlock testid="stat-validees" titre="Validées" accent="border-l-emerald-600" items={[['Opérations', fmt(validees.length)], ['Montant (TND)', fmt(validees.reduce((s, o) => s + o.montant, 0), 3)]]} />
        <StatBlock testid="stat-total-ops" titre="Total saisi" accent="border-l-slate-900" items={[['Lignes', fmt(ops.length)]]} />
      </div>

      <form onSubmit={submit} data-testid="form-depense" className="border border-slate-200 bg-white">
        <div className="grid grid-cols-1 gap-px border-b border-slate-200 bg-slate-200 sm:grid-cols-3">
          {TYPES_OPERATION.map((t) => {
            const actif = f.typeOperation === t.id;
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`type-operation-${t.id}`}
                onClick={() => setF((p) => ({ ...p, typeOperation: t.id }))}
                className={`px-3 py-3 text-left transition-colors duration-150 ${actif ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
              >
                <p className="text-sm font-bold">{t.label}</p>
                <p className={`text-xs ${actif ? 'text-slate-300' : 'text-slate-500'}`}>{t.hint}</p>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className={label}>Date</label>
            <input data-testid="depense-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={label}>Montant (TND)</label>
            <input data-testid="depense-montant" type="number" step="0.001" min="0" value={f.montant} onChange={(e) => setF({ ...f, montant: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={label}>Caisse / source de trésorerie *</label>
            <select data-testid="depense-compte" value={f.compteId} onChange={(e) => setF({ ...f, compteId: e.target.value })} disabled={Boolean(caisseFixe)} className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-700`}>
              {!caisseFixe && <option value="">— D’où sort l’argent ? —</option>}
              {comptes.map((c) => (
                <option key={c.id} value={c.id}>{`${c.nom} — ${fmt(c.solde, 3)} TND`}</option>
              ))}
            </select>
          </div>

          {f.typeOperation === 'frais' && (
            <div>
              <label className={label}>Catégorie</label>
              <SelectCategorieDepense usage="depenses" data-testid="depense-categorie" value={f.categorie} onChange={(e) => setF({ ...f, categorie: e.target.value })} className={inputCls}>
                <option value="">— Choisir —</option>
              </SelectCategorieDepense>
            </div>
          )}

          {f.typeOperation === 'tiers' && (
            <>
              <div>
                <label className={label}>Tiers (client / fournisseur)</label>
                <ContactCombobox contacts={tiers} value={f.tiersId} onChange={(id) => setF({ ...f, tiersId: id })} testid="depense-tiers" />
              </div>
              <div>
                <label className={label}>Nature</label>
                <select data-testid="depense-sens-tiers" value={f.sensTiers} onChange={(e) => setF({ ...f, sensTiers: e.target.value })} className={inputCls}>
                  {SENS_TIERS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {f.typeOperation === 'personnel' && (
            <>
              <div>
                <label className={label}>Employé</label>
                <ContactCombobox
                  contacts={personnel}
                  value={f.employeId}
                  onChange={choisirEmploye}
                  testid="depense-employe"
                  placeholder="Rechercher : nom, CIN, CNSS…"
                  detail={(c) => [c.contrat, c.cin && `CIN ${c.cin}`, c.cnss && `CNSS ${c.cnss}`].filter(Boolean).join(' · ')}
                />
                {soldeEmploye !== null && (
                  <p data-testid="solde-employe" className="mt-1.5 font-data text-[11px] text-slate-600">
                    Solde dû : {fmt(Math.max(0, soldeEmploye), 3)} TND
                  </p>
                )}
              </div>
              <div>
                <label className={label}>Motif</label>
                <select data-testid="depense-motif-paie" value={f.motifPaie} onChange={(e) => setF({ ...f, motifPaie: e.target.value })} className={inputCls}>
                  {MOTIFS_PAIE.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className={f.typeOperation === 'frais' ? '' : 'md:col-span-2'}>
            <label className={label}>Motif / détail</label>
            <input data-testid="depense-motif" value={f.libelle} onChange={(e) => setF({ ...f, libelle: e.target.value })} className={inputCls} placeholder="ex. Gasoil camion, acompte livraison…" />
          </div>

          <div>
            <label className={label}>Photo du reçu</label>
            <label
              data-testid="depense-photo-label"
              className={`mt-1.5 flex h-11 cursor-pointer items-center justify-center gap-2 border px-3 text-sm font-semibold transition-colors duration-150 ${
                f.justificatif ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              <Camera size={15} />
              {f.justificatif ? 'Reçu attaché' : 'Prendre / choisir'}
              <input data-testid="depense-photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => charger(e.target.files?.[0])} />
            </label>
          </div>
        </div>

        <div className="border-t border-slate-200 px-4 py-4">
          <button
            type="submit"
            data-testid="depense-submit"
            className="flex h-12 w-full items-center justify-center gap-2 bg-slate-900 px-4 font-heading text-sm font-bold uppercase tracking-[0.06em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            <Plus size={16} /> Envoyer pour validation
          </button>
          <p className="mt-2 text-center text-xs text-slate-500">
            L'opération reste « En attente » : elle n'affecte ni la caisse, ni les comptes tiers, ni les
            salaires avant validation du gérant.
          </p>
        </div>
      </form>

      {f.justificatif && (
        <div data-testid="depense-apercu" className="flex items-center gap-3 border border-slate-200 bg-white px-4 py-3">
          <img src={f.justificatif} alt="Aperçu du reçu" className="h-20 w-20 border border-slate-200 object-cover" />
          <button
            type="button"
            data-testid="depense-photo-retirer"
            onClick={() => setF({ ...f, justificatif: null })}
            className="border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            Retirer la photo
          </button>
        </div>
      )}

      <section className="border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <p className="font-heading text-base font-bold tracking-tight text-slate-900">Mes opérations</p>
          <div className="flex flex-wrap items-center gap-3">
            <PeriodeFiltre du={du} au={au} setDu={setDu} setAu={setAu} testid="periode-depenses" total={ops.length} filtres={opsVisibles.length} />
            <button
              type="button"
              data-testid="depenses-export-csv"
              onClick={exportOps}
              className="flex h-10 items-center gap-2 border border-slate-300 px-3 text-sm font-semibold text-slate-800 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              <Download size={15} /> Exporter CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left" data-testid="table-mes-depenses">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {['Réf.', 'Date', 'Type', 'Bénéficiaire / catégorie', 'Motif', 'Reçu', 'Montant (TND)', 'Statut'].map((c) => (
                  <th key={c} className={th}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {opsVisibles.length === 0 ? (
                <tr data-testid="mes-depenses-empty">
                  <td colSpan={8} className="px-3 py-10 text-center text-sm text-slate-500">Aucune opération sur cette période.</td>
                </tr>
              ) : (
                opsVisibles.map((o) => (
                  <tr key={o.id} data-testid={`ma-depense-${o.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-data text-sm font-semibold text-slate-900">{o.id}</td>
                    <td className="px-3 py-2 font-data text-sm text-slate-700">{o.date}</td>
                    <td className="px-3 py-2 text-sm text-slate-800">
                      {TYPES_OPERATION.find((t) => t.id === o.typeOperation)?.label}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">
                      {o.typeOperation === 'tiers'
                        ? `${nomTiers(o.tiersId)} · ${o.sensTiers}`
                        : o.typeOperation === 'personnel'
                          ? `${nomTiers(o.employeId)} · ${o.motifPaie}`
                          : (o.categorie ?? '—')}
                    </td>
                    <td className="px-3 py-2 text-sm text-slate-700">{o.libelle}</td>
                    <td className="px-3 py-2">
                      {o.justificatif ? (
                        <button type="button" onClick={() => setApercu(o)} className="border border-slate-300 p-1 transition-colors duration-150 hover:border-slate-900">
                          <img src={o.justificatif} alt="Reçu" className="h-8 w-8 object-cover" />
                        </button>
                      ) : (
                        <span className="font-data text-xs text-amber-700">manquant</span>
                      )}
                    </td>
                    <td className="tabular px-3 py-2 font-data text-sm font-semibold text-slate-900">{fmt(o.montant, 3)}</td>
                    <td className="px-3 py-2">
                      <span data-testid={`statut-${o.id}`} className={`font-data text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 ${COULEURS[o.statut]}`}>
                        {o.statut}
                      </span>
                      {o.motifRefus && (
                        <p data-testid={`refus-${o.id}`} className="mt-1 text-[11px] text-red-700">{o.motifRefus}</p>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {apercu && (
        <div data-testid="depense-apercu-modal" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 p-4" onClick={() => setApercu(null)}>
          <img src={apercu.justificatif} alt="Justificatif" className="max-h-[90vh] max-w-full border-4 border-white object-contain" />
        </div>
      )}
    </div>
  );
}
